/*
 * CollectionsPage — the reference lookbook grid, wired to the real Collections API.
 * DOM/classes/copy kept 1:1; demo COLS → vaultApi listCollections(). Cards navigate
 * to /collections/:id (fixing the old go("collection1") stub), the kebab renames /
 * deletes, and "+ New collection" creates a real collection then opens it.
 *
 * Covers: /collections returns fileIds only, so we fetch listModels() ONCE (one call
 * for the whole grid, not N×4) and map coverFileIds → real posterUrls; a piece with
 * no poster falls back to a procedural placeholder.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { SPRING_PRESS, cardItem, cardStagger } from "@/shared/motion/transitions";
import { Thumb } from "../vault/Common";
import { promptDialog, confirmDialog } from "@/shared/ui";
import { listModels, type VaultModel } from "@/features/vault/vaultApi";
import {
  listCollections,
  createCollection,
  updateCollection,
  deleteCollection,
  type Collection,
} from "@/features/collections/collectionsApi";

const PLACEHOLDER: [string, string][] = [
  ["ringGold", "bgWarm"], ["diaSol", "bgCool"], ["necklace", "bgDark"], ["emerald", "bgBlue"],
];

/** A collection's 4-up cover mosaic from real item posters, placeholder where absent. */
function Cover({ c, models }: { c: Collection; models: Map<string, VaultModel> }) {
  const ids = (c.coverFileIds.length ? c.coverFileIds : c.items.map((i) => i.fileId)).slice(0, 4);
  const cells = ids.length ? ids : [];
  return (
    <div className="col-cover">
      {cells.length === 0
        ? PLACEHOLDER.map(([a, b], i) => <Thumb key={i} art={a} bg={b} />)
        : cells.map((fid, i) => {
            const poster = models.get(fid)?.posterUrl;
            const [a, b] = PLACEHOLDER[i % PLACEHOLDER.length];
            return poster
              ? <div key={fid} className="thumb"><img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /></div>
              : <Thumb key={fid} art={a} bg={b} />;
          })}
    </div>
  );
}

export default function CollectionsPage() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [cols, setCols] = useState<Collection[] | null>(null);
  const [models, setModels] = useState<Map<string, VaultModel>>(new Map());
  const [menu, setMenu] = useState<string | null>(null);

  const load = useCallback(() => {
    listCollections().then(setCols).catch(() => setCols([]));
    listModels(null, {}).then((ms) => setModels(new Map(ms.map((m) => [m._id, m])))).catch(() => undefined);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = await promptDialog({ title: "New collection", label: "Collection name", confirmLabel: "Create" });
    if (!name || !name.trim()) return;
    try {
      const c = await createCollection({ name: name.trim() });
      navigate(`/collections/${c._id}`);
    } catch { /* surfaced by the network layer; stay on page */ }
  };

  const rename = async (c: Collection) => {
    setMenu(null);
    const name = await promptDialog({ title: "Rename collection", label: "Name", defaultValue: c.name });
    if (!name || !name.trim() || name.trim() === c.name) return;
    try { await updateCollection(c._id, { name: name.trim() }); load(); } catch { /* no-op */ }
  };
  const remove = async (c: Collection) => {
    setMenu(null);
    if (!(await confirmDialog({ title: `Delete “${c.name}”?`, body: "This can’t be undone. (Pieces stay in your drive.)", danger: true, confirmLabel: "Delete" }))) return;
    try { await deleteCollection(c._id); load(); } catch { /* no-op */ }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="page-title">Collections</div>
          <div className="page-sub">Curate pieces from anywhere in your drive into a lookbook — one link, your branding, tracked like any share. The same model can live in many collections.</div>
        </div>
        <button className="btn primary" onClick={create}>+ New collection</button>
      </div>

      {cols === null ? (
        <div className="pg-empty" style={{ marginTop: 16 }}>Loading collections…</div>
      ) : (
        <motion.div className="col-grid" variants={cardStagger} initial="hidden" animate="show">
          {cols.map((c, i) => (
            <motion.div
              key={c._id}
              className="col-card"
              onClick={() => navigate(`/collections/${c._id}`)}
              variants={reduce ? undefined : cardItem}
              custom={i}
              whileHover={reduce ? undefined : { y: -3 }}
              transition={SPRING_PRESS}
            >
              <Cover c={c} models={models} />
              <div className="col-info">
                <b>{c.name}</b>
                <div className="cm">
                  {c.items.length} {c.items.length === 1 ? "piece" : "pieces"}
                  {c.shareEnabled && c.sharedClientName
                    ? <> · <span className="badge gold">Shared → {c.sharedClientName}</span></>
                    : c.shareEnabled
                      ? <> · <span className="badge green">Shared link</span></>
                      : <> · <span className="badge gray">Private draft</span></>}
                  {c.viewCount > 0 ? <> · {c.viewCount.toLocaleString()} views</> : null}
                </div>
              </div>
              <button className="kebab" style={{ position: 'absolute', top: 8, right: 8, opacity: 1, background: 'rgba(255,255,255,.92)' }} onClick={e => { e.stopPropagation(); setMenu(menu === c._id ? null : c._id); }}>⋮</button>
              {menu === c._id && (
                <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 34, right: 8, zIndex: 5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6, minWidth: 140 }}>
                  <button className="nav-item" style={{ width: '100%' }} onClick={() => rename(c)}>Rename…</button>
                  <button className="nav-item" style={{ width: '100%', color: 'var(--red)' }} onClick={() => remove(c)}>Delete</button>
                </div>
              )}
            </motion.div>
          ))}
          <div className="col-new" onClick={create}>
            <div style={{ opacity: .7 }}><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="3" width="8" height="8" rx="2"/><rect x="13" y="3" width="8" height="8" rx="2"/><rect x="3" y="13" width="8" height="8" rx="2"/><path d="M17 13.5v7M13.5 17h7"/></svg></div>
            <b style={{ fontSize: 13 }}>New collection</b>
            <span style={{ fontSize: 11.5, maxWidth: 180, textAlign: 'center' }}>Name it, then add pieces from any folder — originals stay where they are</span>
          </div>
        </motion.div>
      )}

      <div className="anno" style={{ marginTop: 16 }}>ⓘ Collections don't move or copy files — they reference them. Update a model once and every collection &amp; live share link shows the new version.</div>
    </>
  );
}
